import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    Clock, Users, Calendar, BarChart3, CreditCard, Plus, Loader2,
    CheckCircle, XCircle, MapPin, Shield, AlertTriangle,
    Play, Coffee, LogOut as LogOutIcon, Building2, Navigation,
    TrendingUp, UserCheck, FileText, Settings,
    ClipboardList, Eye, Filter, Download, AlertOctagon, UserX, Send, Bell, FileDown, Mail
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';


const PONTO_STEPS = [
    { target: '[data-testid="ponto-dashboard"]', title: 'Painel do Ponto', content: 'Visao geral da presenca da equipe em tempo real. Veja quem marcou ponto e alertas do dia.' },
    { target: '[data-testid="bater-ponto-tab"]', title: 'Bater Ponto', content: 'Seus colaboradores registram entrada e saida aqui. O sistema valida localizacao por GPS automaticamente.' },
    { target: '[data-testid="gps-status"]', title: 'Status GPS', content: 'O indicador mostra se o colaborador esta dentro da area permitida (geofence). Verde = dentro, vermelho = fora.' },
];

// ==================== MAIN PAGE ====================
export default function PontoPage() {
    const { user } = useAuth();
    const [empresa, setEmpresa] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState(null);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const acType = user?.account_type || 'platform';
    const isClient = acType === 'client' || acType === 'empresa';
    const isEmployee = acType === 'client_user';
    const isGestor = isEmployee && user?.role === 'gestor';
    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        fetch(`${API}/ponto/empresa`, { headers })
            .then(r => r.json())
            .then(emp => { if (emp && emp.empresa_id) setEmpresa(emp); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (isEmployee && !isGestor) setTab('ponto');
        else setTab('dashboard');
    }, [isEmployee, isGestor]);

    const criarEmpresa = async () => {
        const name = prompt('Nome da empresa:');
        if (!name) return;
        try {
            const res = await fetch(`${API}/ponto/empresa`, { method: 'POST', headers, body: JSON.stringify({ name }) });
            if (res.ok) { const e = await res.json(); setEmpresa(e); toast.success('Empresa criada!'); }
            else { const d = await res.json(); toast.error(d.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); }
    };

    if (loading) return <div className="flex justify-center items-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>;
    if (!empresa) return <NoEmpresaView isClient={isClient} isAdmin={isAdmin} onCriar={criarEmpresa} />;

    const clientTabs = [
        { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { key: 'ponto', label: 'Bater Ponto', icon: Clock },
        { key: 'funcionarios', label: 'Equipe', icon: Users },
        { key: 'registros', label: 'Registros', icon: Calendar },
        { key: 'ajustes', label: 'Ajustes', icon: ClipboardList },
        { key: 'auditoria', label: 'Auditoria', icon: Eye },
        { key: 'relatórios', label: 'Relatórios', icon: FileText },
        { key: 'jornadas', label: 'Jornadas', icon: TrendingUp },
        { key: 'planos', label: 'Plano', icon: CreditCard },
        { key: 'config', label: 'Config', icon: Settings },
    ];

    const gestorTabs = [
        { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { key: 'ponto', label: 'Bater Ponto', icon: Clock },
        { key: 'registros', label: 'Registros', icon: Calendar },
        { key: 'ajustes', label: 'Ajustes', icon: ClipboardList },
        { key: 'histórico', label: 'Histórico', icon: Calendar },
    ];

    const employeeTabs = [
        { key: 'ponto', label: 'Bater Ponto', icon: Clock },
        { key: 'histórico', label: 'Histórico', icon: Calendar },
        { key: 'ajustes', label: 'Meus Ajustes', icon: ClipboardList },
    ];

    const tabs = isClient || isAdmin ? clientTabs : isGestor ? gestorTabs : employeeTabs;

    return (
        <div className="p-3 sm:p-6 max-w-5xl mx-auto" data-testid="ponto-page">
            <ContextualTour pageKey="ponto" steps={PONTO_STEPS} />
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)' }}>
                        <Clock size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                    </div>
                    <div>
                        <h2 className="text-base sm:text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }} data-testid="ponto-title">Ponto Digital</h2>
                        <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{empresa.name}</p>
                    </div>
                </div>
                <span className="text-[10px] px-2 py-1 rounded-full font-medium" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>
                    {empresa.plan?.charAt(0).toUpperCase() + empresa.plan?.slice(1)}
                </span>
            </div>

            <div className="flex gap-1 mb-5 overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0" style={{ scrollbarWidth: 'none' }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all"
                        style={{ background: tab === t.key ? 'var(--sv-gold-primary)' : 'var(--surface)', color: tab === t.key ? '#0A0A0A' : 'var(--muted)', border: tab === t.key ? 'none' : '1px solid var(--border)' }}
                        data-testid={`ponto-tab-${t.key}`}
                    ><t.icon size={13} />{t.label}</button>
                ))}
            </div>

            {tab === 'dashboard' && <DashboardTab headers={headers} empresa={empresa} />}
            {tab === 'ponto' && <BaterPontoTab headers={headers} empresa={empresa} user={user} />}
            {tab === 'funcionarios' && <FuncionariosTab headers={headers} />}
            {tab === 'registros' && <RegistrosTab headers={headers} />}
            {tab === 'histórico' && <HistóricoTab headers={headers} user={user} />}
            {tab === 'ajustes' && <AjustesTab headers={headers} user={user} isClient={isClient || isGestor || isAdmin} />}
            {tab === 'auditoria' && <AuditoriaTab headers={headers} />}
            {tab === 'relatórios' && <RelatóriosTab headers={headers} />}
            {tab === 'jornadas' && <JornadasTab headers={headers} />}
            {tab === 'planos' && <PlanosTab empresa={empresa} setEmpresa={setEmpresa} headers={headers} />}
            {tab === 'config' && <ConfigTab headers={headers} empresa={empresa} setEmpresa={setEmpresa} />}
        </div>
    );
}

// ==================== NO EMPRESA ====================
function NoEmpresaView({ isClient, isAdmin, onCriar }) {
    return (
        <div className="p-6 max-w-md mx-auto text-center py-16" data-testid="ponto-no-empresa">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)' }}>
                <Building2 size={28} style={{ color: 'var(--sv-gold-primary)' }} />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Ponto Digital</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>Controle de ponto com geolocalização, jornadas e relatórios para sua empresa.</p>
            {(isClient || isAdmin) ? (
                <Button onClick={onCriar} className="bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)]" data-testid="criar-empresa-btn"><Plus className="w-4 h-4 mr-2" /> Criar Empresa</Button>
            ) : (
                <p className="text-xs" style={{ color: 'var(--muted-2)' }}>Sua empresa ainda não configurou o Ponto Digital.</p>
            )}
        </div>
    );
}

// ==================== DASHBOARD ENHANCED ====================
function DashboardTab({ headers, empresa }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/ponto/dashboard/extended`, { headers })
            .then(r => r.json()).then(setStats).catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Spinner />;
    if (!stats || stats.error) return null;

    return (
        <div className="space-y-4" data-testid="ponto-dashboard">
            {/* Row 1: Main stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Funcionarios" value={stats.total_employees} icon={Users} sub={`${stats.gestores} gestor(es)`} />
                <StatCard label="Presentes" value={stats.present_today} icon={UserCheck} color="#22c55e" sub={`de ${stats.total_employees}`} />
                <StatCard label="Ausentes" value={stats.absent_today} icon={UserX} color={stats.absent_today > 0 ? '#ef4444' : '#22c55e'} />
                <StatCard label="Registros hoje" value={stats.records_today} icon={Clock} color="#3b82f6" sub={`${stats.entries_today} entradas`} />
            </div>
            {/* Row 2: Alerts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Ajustes pendentes" value={stats.pending_ajustes} icon={ClipboardList} color={stats.pending_ajustes > 0 ? '#eab308' : '#22c55e'} />
                <StatCard label="Bloqueios hoje" value={stats.denied_today} icon={AlertOctagon} color={stats.denied_today > 0 ? '#ef4444' : '#22c55e'} />
                <StatCard label="Fora geofence" value={stats.outside_geofence_today} icon={MapPin} color={stats.outside_geofence_today > 0 ? '#eab308' : '#22c55e'} />
                <StatCard label="Jornadas" value={stats.total_jornadas} icon={TrendingUp} color="var(--sv-gold-primary)" />
            </div>
            {/* Row 3: Status cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-3"><Shield size={14} style={{ color: 'var(--sv-gold-primary)' }} /><span className="text-xs font-medium" style={{ color: 'var(--text)' }}>Plano</span></div>
                    <div className="space-y-1.5">
                        <SRow label="Plano" value={stats.plan?.charAt(0).toUpperCase() + stats.plan?.slice(1)} ok={true} />
                        <SRow label="Status" value={stats.plan_status === 'active' ? 'Ativo' : 'Inativo'} ok={stats.plan_status === 'active'} />
                        <SRow label="Limite" value={`${stats.total_employees}/${stats.max_employees}`} ok={stats.total_employees < stats.max_employees} />
                    </div>
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-3"><MapPin size={14} style={{ color: 'var(--sv-gold-primary)' }} /><span className="text-xs font-medium" style={{ color: 'var(--text)' }}>Seguranca</span></div>
                    <div className="space-y-1.5">
                        <SRow label="Geofence" value={stats.geofence_enabled ? 'Ativo' : 'Desativado'} ok={stats.geofence_enabled} />
                        <SRow label="GPS obrigatorio" value="Entrada" ok={true} />
                        <SRow label="Auditoria" value="Ativa" ok={true} />
                    </div>
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-3"><BarChart3 size={14} style={{ color: 'var(--sv-gold-primary)' }} /><span className="text-xs font-medium" style={{ color: 'var(--text)' }}>Empresa</span></div>
                    <div className="space-y-1.5">
                        <SRow label="Nome" value={stats.empresa_name} ok={true} />
                        <SRow label="Modulo" value="Ponto Digital" ok={true} />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ==================== BATER PONTO ====================
function BaterPontoTab({ headers, empresa, user }) {
    const [time, setTime] = useState(new Date());
    const [geoStatus, setGeoStatus] = useState('loading');
    const [position, setPosition] = useState(null);
    const [accuracy, setAccuracy] = useState(null);
    const [batendo, setBatendo] = useState(false);
    const [todayRecords, setTodayRecords] = useState([]);
    const [lastAction, setLastAction] = useState(null);
    const watchRef = useRef(null);

    useEffect(() => { const i = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(i); }, []);

    useEffect(() => {
        if (!navigator.geolocation) { setGeoStatus('error'); return; }
        setGeoStatus('loading');
        watchRef.current = navigator.geolocation.watchPosition(
            (pos) => { setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setAccuracy(pos.coords.accuracy); setGeoStatus('ok'); },
            (err) => { setGeoStatus(err.code === 1 ? 'denied' : 'error'); },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
        );
        return () => { if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current); };
    }, []);

    useEffect(() => {
        fetch(`${API}/ponto/registros/today`, { headers }).then(r => r.json()).then(data => {
            if (Array.isArray(data)) { setTodayRecords(data); if (data.length > 0) setLastAction(data[data.length - 1]); }
        }).catch(() => {});
    }, []);

    const getDeviceInfo = () => { const ua = navigator.userAgent; return `${/Mobile|Android|iPhone/i.test(ua) ? 'Mobile' : 'Desktop'} - ${navigator.platform || 'Unknown'}`; };
    const getNextAction = () => {
        if (!lastAction) return 'entrada';
        const t = lastAction.tipo;
        if (t === 'entrada') return 'inicio_intervalo';
        if (t === 'inicio_intervalo') return 'fim_intervalo';
        if (t === 'fim_intervalo') return 'saída';
        return 'entrada';
    };

    const bater = async (tipo) => {
        if (tipo === 'entrada' && geoStatus !== 'ok') { toast.error('GPS obrigatorio para registrar entrada.'); return; }
        setBatendo(true);
        try {
            const body = { tipo, latitude: position?.lat || null, longitude: position?.lng || null, accuracy: accuracy || null, device_info: getDeviceInfo(), device_timestamp: new Date().toISOString() };
            const res = await fetch(`${API}/ponto/registros`, { method: 'POST', headers, body: JSON.stringify(body) });
            if (res.ok) {
                const reg = await res.json();
                const labels = { entrada: 'Entrada', inicio_intervalo: 'Inicio Intervalo', fim_intervalo: 'Fim Intervalo', saída: 'Saída' };
                toast.success(`${labels[tipo]} registrada!`);
                setTodayRecords(prev => [...prev, reg]); setLastAction(reg);
            } else { const d = await res.json(); toast.error(d.detail || 'Erro ao registrar'); }
        } catch { toast.error('Erro de conexão'); }
        finally { setBatendo(false); }
    };

    const nextAction = getNextAction();
    const AC = {
        entrada: { label: 'Registrar Entrada', icon: Play, color: '#22c55e', bg: 'rgba(34,197,94,0.1)' },
        inicio_intervalo: { label: 'Inicio Intervalo', icon: Coffee, color: '#eab308', bg: 'rgba(234,179,8,0.1)' },
        fim_intervalo: { label: 'Fim Intervalo', icon: Coffee, color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' },
        saída: { label: 'Registrar Saída', icon: LogOutIcon, color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
    };
    const ma = AC[nextAction];

    return (
        <div className="space-y-4" data-testid="bater-ponto-tab">
            <div className="rounded-2xl p-6 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <p className="text-xs mb-2" style={{ color: 'var(--muted-2)' }}>{time.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}</p>
                <p className="text-5xl sm:text-6xl font-bold tracking-tight" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }} data-testid="ponto-clock">
                    {time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </p>
                <p className="text-xs mt-2" style={{ color: 'var(--muted-2)' }}>{user?.name} - {empresa?.name}</p>
            </div>

            <div className="rounded-xl p-3 flex items-center gap-3" style={{ background: geoStatus === 'ok' ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)', border: `1px solid ${geoStatus === 'ok' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'}` }} data-testid="gps-status">
                {geoStatus === 'loading' && <Loader2 size={16} className="animate-spin" style={{ color: 'var(--muted)' }} />}
                {geoStatus === 'ok' && <Navigation size={16} style={{ color: '#22c55e' }} />}
                {(geoStatus === 'denied' || geoStatus === 'error') && <AlertTriangle size={16} style={{ color: '#ef4444' }} />}
                <p className="text-xs font-medium flex-1" style={{ color: geoStatus === 'ok' ? '#22c55e' : '#ef4444' }}>
                    {geoStatus === 'loading' ? 'Obtendo localização...' : geoStatus === 'ok' ? `GPS ativo - Precisao: ${accuracy?.toFixed(0)}m` : geoStatus === 'denied' ? 'GPS bloqueado' : 'GPS indisponível'}
                </p>
            </div>

            <button onClick={() => bater(nextAction)} disabled={batendo || (nextAction === 'entrada' && geoStatus !== 'ok')}
                className="w-full rounded-2xl p-5 flex items-center justify-center gap-3 transition-all active:scale-[0.98]"
                style={{ background: ma.bg, border: `2px solid ${ma.color}30`, opacity: batendo || (nextAction === 'entrada' && geoStatus !== 'ok') ? 0.5 : 1 }}
                data-testid={`bater-${nextAction}`}>
                {batendo ? <Loader2 size={24} className="animate-spin" style={{ color: ma.color }} /> : <ma.icon size={24} style={{ color: ma.color }} />}
                <span className="text-base font-bold" style={{ color: ma.color }}>{ma.label}</span>
            </button>

            <div className="grid grid-cols-4 gap-2">
                {Object.entries(AC).map(([key, cfg]) => (
                    <button key={key} onClick={() => bater(key)} disabled={batendo}
                        className="rounded-xl p-3 flex flex-col items-center gap-1.5 transition-all active:scale-95"
                        style={{ background: 'var(--surface)', border: '1px solid var(--border)', opacity: batendo ? 0.5 : 1 }}
                        data-testid={`bater-alt-${key}`}>
                        <cfg.icon size={16} style={{ color: cfg.color }} />
                        <span className="text-[9px] font-medium" style={{ color: 'var(--muted)' }}>
                            {key === 'entrada' ? 'Entrada' : key === 'inicio_intervalo' ? 'Intervalo' : key === 'fim_intervalo' ? 'Retorno' : 'Saída'}
                        </span>
                    </button>
                ))}
            </div>

            {todayRecords.length > 0 && (
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <p className="text-xs font-medium mb-3" style={{ color: 'var(--text)' }}>Marcações de hoje</p>
                    <div className="space-y-2">
                        {todayRecords.map((r, i) => (
                            <div key={r.registro_id || i} className="flex items-center gap-3">
                                <TipoIcon tipo={r.tipo} />
                                <span className="text-xs flex-1" style={{ color: 'var(--text)' }}>{tipoLabel(r.tipo)}</span>
                                <span className="text-xs font-mono" style={{ color: 'var(--muted-2)' }}>{fmtTime(r.timestamp_server)}</span>
                                {r.inside_geofence !== null && <GeoBadge inside={r.inside_geofence} />}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ==================== HISTORICO (Employee) ====================
function HistóricoTab({ headers, user }) {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API}/ponto/registros`, { headers }).then(r => r.json()).then(d => { if (Array.isArray(d)) setRegistros(d); }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) return <Spinner />;

    const grouped = {};
    registros.forEach(r => { const date = r.timestamp_server?.split('T')[0] || ''; if (!grouped[date]) grouped[date] = []; grouped[date].push(r); });

    return (
        <div className="space-y-3" data-testid="histórico-tab">
            {Object.keys(grouped).length === 0 && <EmptyState icon={Calendar} text="Nenhum registro encontrado" />}
            {Object.entries(grouped).map(([date, recs]) => (
                <div key={date} className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="px-4 py-2" style={{ background: 'var(--surface-2)' }}>
                        <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{fmtDate(date)}</p>
                    </div>
                    {recs.map((r, i) => (
                        <div key={r.registro_id || i} className="flex items-center gap-3 px-4 py-2.5" style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                            <TipoIcon tipo={r.tipo} />
                            <span className="text-xs flex-1" style={{ color: 'var(--text)' }}>{tipoLabel(r.tipo)}</span>
                            <span className="text-xs font-mono" style={{ color: 'var(--muted-2)' }}>{fmtTime(r.timestamp_server)}</span>
                            {r.latitude && <MapPin size={10} style={{ color: '#22c55e' }} />}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

// ==================== AJUSTES ====================
function AjustesTab({ headers, user, isClient }) {
    const [ajustes, setAjustes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ data: '', tipo: 'entrada', horário_solicitado: '', motivo: '' });
    const [saving, setSaving] = useState(false);

    const fetchAjustes = useCallback(() => {
        setLoading(true);
        fetch(`${API}/ponto/ajustes`, { headers }).then(r => r.json()).then(d => { if (Array.isArray(d)) setAjustes(d); }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchAjustes(); }, [fetchAjustes]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.data || !form.horário_solicitado || !form.motivo) { toast.error('Preencha todos os campos'); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API}/ponto/ajustes`, { method: 'POST', headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success('Ajuste solicitado!'); setShowForm(false); setForm({ data: '', tipo: 'entrada', horário_solicitado: '', motivo: '' }); fetchAjustes(); }
            else { const d = await res.json(); toast.error(d.detail || 'Erro'); }
        } catch { toast.error('Erro'); }
        finally { setSaving(false); }
    };

    const handleResolve = async (id, status) => {
        try {
            const res = await fetch(`${API}/ponto/ajustes/${id}`, { method: 'PUT', headers, body: JSON.stringify({ status }) });
            if (res.ok) { toast.success(status === 'aprovado' ? 'Aprovado!' : 'Rejeitado'); fetchAjustes(); }
        } catch { toast.error('Erro'); }
    };

    if (loading) return <Spinner />;

    const pendentes = ajustes.filter(a => a.status === 'pendente');
    const resolvidos = ajustes.filter(a => a.status !== 'pendente');

    return (
        <div data-testid="ponto-ajustes-tab">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>{pendentes.length} pendente(s)</span>
                </div>
                <Button onClick={() => setShowForm(!showForm)} className="bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" data-testid="solicitar-ajuste-btn">
                    <Plus className="w-4 h-4 mr-1" /> Solicitar
                </Button>
            </div>

            {showForm && (
                <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)' }}>
                    <form onSubmit={handleCreate} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Data</label>
                                <input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ajuste-data" /></div>
                            <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Horário</label>
                                <input type="time" value={form.horário_solicitado} onChange={e => setForm({...form, horário_solicitado: e.target.value})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ajuste-horário" /></div>
                        </div>
                        <select value={form.tipo} onChange={e => setForm({...form, tipo: e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ajuste-tipo">
                            <option value="entrada">Entrada</option><option value="inicio_intervalo">Inicio Intervalo</option>
                            <option value="fim_intervalo">Fim Intervalo</option><option value="saída">Saída</option>
                        </select>
                        <textarea placeholder="Motivo do ajuste..." value={form.motivo} onChange={e => setForm({...form, motivo: e.target.value})} rows={2}
                            className="w-full rounded-lg px-3 py-2.5 text-sm resize-none" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ajuste-motivo" />
                        <Button type="submit" className="w-full bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar Solicitação'}</Button>
                    </form>
                </div>
            )}

            {/* Pendentes */}
            {pendentes.length > 0 && (
                <div className="mb-4">
                    <p className="text-xs font-medium mb-2" style={{ color: 'var(--text)' }}>Pendentes</p>
                    <div className="space-y-2">
                        {pendentes.map(a => (
                            <div key={a.ajuste_id} className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid rgba(234,179,8,0.3)' }} data-testid={`ajuste-${a.ajuste_id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(234,179,8,0.1)', color: '#eab308' }}>Pendente</span>
                                    <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{a.user_name}</span>
                                    <span className="text-[10px] ml-auto" style={{ color: 'var(--muted-2)' }}>{a.data} - {a.horário_solicitado}</span>
                                </div>
                                <p className="text-[11px] mb-2" style={{ color: 'var(--muted)' }}>{tipoLabel(a.tipo)} - {a.motivo}</p>
                                {isClient && (
                                    <div className="flex gap-2">
                                        <Button onClick={() => handleResolve(a.ajuste_id, 'aprovado')} size="sm" className="bg-green-600 hover:bg-green-700 text-white flex-1 text-[10px]" data-testid={`aprovar-ajuste-${a.ajuste_id}`}>Aprovar</Button>
                                        <Button onClick={() => handleResolve(a.ajuste_id, 'rejeitado')} size="sm" variant="outline" className="border-red-500/50 text-red-400 flex-1 text-[10px]" data-testid={`rejeitar-ajuste-${a.ajuste_id}`}>Rejeitar</Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Resolvidos */}
            {resolvidos.length > 0 && (
                <div>
                    <p className="text-xs font-medium mb-2" style={{ color: 'var(--text)' }}>Resolvidos</p>
                    <div className="space-y-2">
                        {resolvidos.map(a => (
                            <div key={a.ajuste_id} className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{
                                    background: a.status === 'aprovado' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                                    color: a.status === 'aprovado' ? '#22c55e' : '#ef4444'
                                }}>{a.status === 'aprovado' ? 'Aprovado' : 'Rejeitado'}</span>
                                <span className="text-xs flex-1" style={{ color: 'var(--text)' }}>{a.user_name} - {tipoLabel(a.tipo)}</span>
                                <span className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{a.data}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {ajustes.length === 0 && <EmptyState icon={ClipboardList} text="Nenhum ajuste solicitado" />}
        </div>
    );
}

// ==================== AUDITORIA ====================
function AuditoriaTab({ headers }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');

    const fetchLogs = useCallback(() => {
        setLoading(true);
        const qs = filterStatus ? `?status=${filterStatus}` : '';
        fetch(`${API}/ponto/audit-logs${qs}`, { headers }).then(r => r.json()).then(d => { if (Array.isArray(d)) setLogs(d); }).catch(() => {}).finally(() => setLoading(false));
    }, [filterStatus]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    if (loading) return <Spinner />;

    return (
        <div data-testid="ponto-auditoria-tab">
            <div className="flex items-center gap-2 mb-4">
                <Filter size={14} style={{ color: 'var(--muted-2)' }} />
                {['', 'approved', 'denied'].map(s => (
                    <button key={s} onClick={() => setFilterStatus(s)}
                        className="text-[10px] px-2 py-1 rounded-full transition-all"
                        style={{ background: filterStatus === s ? 'var(--sv-gold-primary)' : 'var(--surface)', color: filterStatus === s ? '#0A0A0A' : 'var(--muted)', border: filterStatus === s ? 'none' : '1px solid var(--border)' }}>
                        {s === '' ? 'Todos' : s === 'approved' ? 'Aprovados' : 'Bloqueados'}
                    </button>
                ))}
                <span className="text-[10px] ml-auto" style={{ color: 'var(--muted-2)' }}>{logs.length} registros</span>
            </div>

            {logs.length === 0 ? <EmptyState icon={Eye} text="Nenhum log de auditoria" /> : (
                <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    {logs.map((l, i) => (
                        <div key={l.log_id || i} className="flex items-center gap-3 px-4 py-3" style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }} data-testid={`audit-${l.log_id}`}>
                            {l.status === 'denied' ? <XCircle size={14} style={{ color: '#ef4444' }} /> : <CheckCircle size={14} style={{ color: '#22c55e' }} />}
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{l.user_name || l.user_id}</p>
                                <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>
                                    {tipoLabel(l.tipo)} {l.reason ? `- ${l.reason}` : ''}
                                </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <p className="text-[10px] font-mono" style={{ color: 'var(--muted-2)' }}>{fmtTime(l.timestamp_server)}</p>
                                <p className="text-[9px]" style={{ color: 'var(--muted-2)' }}>{l.timestamp_server?.split('T')[0]?.split('-').reverse().join('/')}</p>
                            </div>
                            {l.latitude && <MapPin size={9} style={{ color: '#22c55e' }} />}
                            {l.inside_geofence !== null && l.inside_geofence !== undefined && <GeoBadge inside={l.inside_geofence} />}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ==================== RELATORIOS ====================
function RelatóriosTab({ headers }) {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    const fetchReport = () => {
        setLoading(true);
        const qs = new URLSearchParams();
        if (dataInicio) qs.set('data_inicio', dataInicio);
        if (dataFim) qs.set('data_fim', dataFim);
        fetch(`${API}/ponto/relatórios?${qs}`, { headers }).then(r => r.json()).then(setReport).catch(() => {}).finally(() => setLoading(false));
    };

    useEffect(() => { fetchReport(); }, []);

    return (
        <div data-testid="ponto-relatórios-tab">
            {/* Filters */}
            <div className="flex gap-2 mb-4 flex-wrap">
                <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>De</label>
                    <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="rel-data-inicio" /></div>
                <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Ate</label>
                    <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="rel-data-fim" /></div>
                <div className="flex items-end">
                    <Button onClick={fetchReport} className="bg-[var(--sv-secondary)] hover:bg-[var(--sv-secondary-hover)] text-[var(--sv-on-secondary)] text-xs" data-testid="rel-filtrar">
                        <Filter className="w-3 h-3 mr-1" /> Filtrar
                    </Button>
                </div>
            </div>

            {loading ? <Spinner /> : report && !report.error ? (
                <div className="space-y-4">
                    {/* Stats summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <StatCard label="Dias" value={report.stats.total_days} icon={Calendar} />
                        <StatCard label="Funcionarios" value={report.stats.total_users} icon={Users} />
                        <StatCard label="Registros" value={report.stats.total_records} icon={FileText} color="#3b82f6" />
                        <StatCard label="Media horas" value={`${report.stats.avg_hours}h`} icon={Clock} color="#22c55e" />
                    </div>

                    {/* Table */}
                    {report.summary.length > 0 ? (
                        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <div className="grid grid-cols-5 gap-2 px-4 py-2 text-[10px] font-medium" style={{ background: 'var(--surface-2)', color: 'var(--muted-2)' }}>
                                <span>Funcionario</span><span>Data</span><span>Registros</span><span>Horas</span><span>Status</span>
                            </div>
                            {report.summary.slice(0, 50).map((s, i) => (
                                <div key={i} className="grid grid-cols-5 gap-2 px-4 py-2.5 items-center" style={{ borderTop: '1px solid var(--border)' }}>
                                    <span className="text-xs truncate" style={{ color: 'var(--text)' }}>{s.user_name || s.user_id.slice(-6)}</span>
                                    <span className="text-[10px] font-mono" style={{ color: 'var(--muted-2)' }}>{s.date.split('-').reverse().join('/')}</span>
                                    <span className="text-xs" style={{ color: 'var(--text)' }}>{s.records_count}</span>
                                    <span className="text-xs font-medium" style={{ color: s.hours_worked ? '#22c55e' : 'var(--muted-2)' }}>{s.hours_worked ? `${s.hours_worked}h` : '-'}</span>
                                    <div className="flex gap-1">
                                        {s.has_entrada && <span className="text-[8px] px-1 rounded" style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e' }}>E</span>}
                                        {s.has_saída && <span className="text-[8px] px-1 rounded" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>S</span>}
                                        {!s.has_saída && s.has_entrada && <span className="text-[8px] px-1 rounded" style={{ background: 'rgba(234,179,8,0.1)', color: '#eab308' }}>Aberto</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : <EmptyState icon={FileText} text="Nenhum dado no período selecionado" />}
                </div>
            ) : null}
        </div>
    );
}

// ==================== FUNCIONARIOS ====================
function FuncionariosTab({ headers }) {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', email: '', password: '', role: 'funcionario' });
    const [saving, setSaving] = useState(false);

    const fetchEmployees = useCallback(() => {
        setLoading(true);
        fetch(`${API}/client/users`, { headers }).then(r => r.json()).then(d => { if (Array.isArray(d)) setEmployees(d); }).catch(() => {}).finally(() => setLoading(false));
    }, []);
    useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.password) { toast.error('Preencha todos os campos'); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API}/client/users`, { method: 'POST', headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success('Cadastrado!'); setShowForm(false); setForm({ name: '', email: '', password: '', role: 'funcionario' }); fetchEmployees(); }
            else { const d = await res.json(); toast.error(d.detail || 'Erro'); }
        } catch { toast.error('Erro'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Remover ${name}?`)) return;
        try {
            const res = await fetch(`${API}/client/users/${id}`, { method: 'DELETE', headers });
            if (res.ok) { toast.success('Removido'); setEmployees(employees.filter(e => e.user_id !== id)); }
        } catch { toast.error('Erro'); }
    };

    if (loading) return <Spinner />;

    return (
        <div data-testid="ponto-funcionarios-tab">
            <div className="flex items-center justify-between mb-4">
                <span className="text-xs" style={{ color: 'var(--muted)' }}>{employees.length} funcionario(s)</span>
                <Button onClick={() => setShowForm(!showForm)} className="bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" data-testid="ponto-add-func">
                    <Plus className="w-4 h-4 mr-1" /> Novo
                </Button>
            </div>
            {showForm && (
                <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)' }}>
                    <form onSubmit={handleCreate} className="space-y-3">
                        <input type="text" placeholder="Nome completo" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ponto-func-name" />
                        <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ponto-func-email" />
                        <input type="password" placeholder="Senha" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ponto-func-password" />
                        <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="ponto-func-role">
                            <option value="funcionario">Funcionario</option><option value="gestor">Gestor</option>
                        </select>
                        <div className="flex gap-2">
                            <Button type="submit" className="flex-1 bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cadastrar'}</Button>
                            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>Cancelar</Button>
                        </div>
                    </form>
                </div>
            )}
            <div className="space-y-2">
                {employees.map(emp => (
                    <div key={emp.user_id} className="flex items-center gap-3 rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`ponto-func-${emp.user_id}`}>
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'var(--surface-2)', color: 'var(--sv-gold-primary)' }}>{emp.name?.charAt(0).toUpperCase()}</div>
                        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{emp.name}</p><p className="text-[10px] truncate" style={{ color: 'var(--muted-2)' }}>{emp.email}</p></div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: emp.role === 'gestor' ? 'rgba(var(--sv-accent-rgb), 0.12)' : 'var(--surface-2)', color: emp.role === 'gestor' ? 'var(--sv-gold-primary)' : 'var(--muted)' }}>{emp.role === 'gestor' ? 'Gestor' : 'Func.'}</span>
                        <button onClick={() => handleDelete(emp.user_id, emp.name)} className="p-1.5 rounded-lg hover:bg-red-500/10" style={{ color: 'var(--muted-2)' }} data-testid={`ponto-del-func-${emp.user_id}`}>
                            <XCircle size={14} />
                        </button>
                    </div>
                ))}
                {employees.length === 0 && <EmptyState icon={Users} text="Nenhum funcionario cadastrado" />}
            </div>
        </div>
    );
}

// ==================== REGISTROS (All) ====================
function RegistrosTab({ headers }) {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { fetch(`${API}/ponto/registros`, { headers }).then(r => r.json()).then(d => { if (Array.isArray(d)) setRegistros(d); }).catch(() => {}).finally(() => setLoading(false)); }, []);
    if (loading) return <Spinner />;
    return (
        <div data-testid="ponto-registros-tab">
            {registros.length === 0 ? <EmptyState icon={Calendar} text="Nenhum registro" /> : (
                <div className="rounded-xl overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    {registros.map((r, i) => (
                        <div key={r.registro_id || i} className="flex items-center gap-3 px-4 py-3" style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                            <TipoIcon tipo={r.tipo} />
                            <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{r.user_name || r.user_id}</p><p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{tipoLabel(r.tipo)}</p></div>
                            <div className="text-right"><p className="text-[10px] font-mono" style={{ color: 'var(--muted-2)' }}>{fmtTime(r.timestamp_server)}</p><p className="text-[9px]" style={{ color: 'var(--muted-2)' }}>{r.timestamp_server?.split('T')[0]?.split('-').reverse().join('/')}</p></div>
                            {r.latitude && <MapPin size={10} style={{ color: '#22c55e' }} />}
                            {r.inside_geofence !== null && r.inside_geofence !== undefined && <GeoBadge inside={r.inside_geofence} />}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ==================== JORNADAS ====================
function JornadasTab({ headers }) {
    const [jornadas, setJornadas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: '', entrada: '08:00', saída: '17:00', intervalo_min: 60, tolerancia_min: 10 });
    const [saving, setSaving] = useState(false);
    useEffect(() => { fetch(`${API}/ponto/jornadas`, { headers }).then(r => r.json()).then(d => { if (Array.isArray(d)) setJornadas(d); }).catch(() => {}).finally(() => setLoading(false)); }, []);

    const handleCreate = async (e) => {
        e.preventDefault(); if (!form.name) { toast.error('Informe o nome'); return; }
        setSaving(true);
        try { const res = await fetch(`${API}/ponto/jornadas`, { method: 'POST', headers, body: JSON.stringify(form) }); if (res.ok) { const j = await res.json(); setJornadas([...jornadas, j]); setShowForm(false); toast.success('Jornada criada!'); } } catch { toast.error('Erro'); }
        finally { setSaving(false); }
    };
    if (loading) return <Spinner />;
    return (
        <div data-testid="ponto-jornadas-tab">
            <div className="flex items-center justify-between mb-4">
                <span className="text-xs" style={{ color: 'var(--muted)' }}>{jornadas.length} jornada(s)</span>
                <Button onClick={() => setShowForm(!showForm)} className="bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs"><Plus className="w-4 h-4 mr-1" /> Nova</Button>
            </div>
            {showForm && (
                <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--surface)', border: '1px solid var(--sv-gold-primary)' }}>
                    <form onSubmit={handleCreate} className="space-y-3">
                        <input type="text" placeholder="Nome (ex: Comercial)" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full rounded-lg px-3 py-2.5 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Entrada</label><input type="time" value={form.entrada} onChange={e => setForm({...form, entrada: e.target.value})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} /></div>
                            <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Saída</label><input type="time" value={form.saida} onChange={e => setForm({...form, saida: e.target.value})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Intervalo (min)</label><input type="number" value={form.intervalo_min} onChange={e => setForm({...form, intervalo_min: parseInt(e.target.value)})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} /></div>
                            <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Tolerancia (min)</label><input type="number" value={form.tolerancia_min} onChange={e => setForm({...form, tolerancia_min: parseInt(e.target.value)})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} /></div>
                        </div>
                        <Button type="submit" className="w-full bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar Jornada'}</Button>
                    </form>
                </div>
            )}
            <div className="space-y-2">
                {jornadas.map(j => (
                    <div key={j.jornada_id} className="rounded-xl p-3 flex items-center gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <FileText size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                        <div className="flex-1"><p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{j.name}</p><p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{j.entrada} - {j.saida} | Int: {j.intervalo_min}min | Tol: {j.tolerancia_min}min</p></div>
                    </div>
                ))}
                {jornadas.length === 0 && <EmptyState icon={FileText} text="Nenhuma jornada configurada" />}
            </div>
        </div>
    );
}

// ==================== PLANOS ====================
function PlanosTab({ headers, empresa, setEmpresa }) {
    const [planos, setPlanos] = useState([]);
    const [planoStatus, setPlanoStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [upgrading, setUpgrading] = useState(false);

    useEffect(() => {
        Promise.all([
            fetch(`${API}/ponto/planos`).then(r => r.json()),
            fetch(`${API}/ponto/empresa/plano`, { headers }).then(r => r.json()),
        ]).then(([p, s]) => { setPlanos(p); setPlanoStatus(s); }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const handleUpgrade = async (planKey) => {
        if (!window.confirm(`Confirmar upgrade para plano ${planKey.charAt(0).toUpperCase() + planKey.slice(1)}?`)) return;
        setUpgrading(true);
        try {
            const res = await fetch(`${API}/ponto/empresa/plano`, { method: 'PUT', headers, body: JSON.stringify({ plan: planKey }) });
            if (res.ok) {
                const d = await res.json();
                toast.success(d.message);
                setPlanoStatus({ ...planoStatus, plan: planKey, max_employees: d.max_employees, plan_expires: d.expires, plan_status: 'active' });
                if (setEmpresa && empresa) setEmpresa({ ...empresa, plan: planKey, max_employees: d.max_employees });
            } else { const d = await res.json(); toast.error(d.detail || 'Erro'); }
        } catch { toast.error('Erro'); }
        finally { setUpgrading(false); }
    };

    if (loading) return <Spinner />;

    return (
        <div data-testid="ponto-planos-tab">
            {/* Current plan status */}
            {planoStatus && (
                <div className="rounded-xl p-4 mb-4" style={{ background: 'var(--surface)', border: '2px solid var(--sv-gold-primary)' }}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <CreditCard size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                            <span className="text-sm font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Seu Plano</span>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{
                            background: planoStatus.plan_status === 'active' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                            color: planoStatus.plan_status === 'active' ? '#22c55e' : '#ef4444'
                        }}>{planoStatus.plan_status === 'active' ? 'Ativo' : 'Inativo'}</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div><p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Plano</p><p className="text-sm font-bold" style={{ color: 'var(--sv-gold-primary)' }}>{planoStatus.plan?.charAt(0).toUpperCase() + planoStatus.plan?.slice(1)}</p></div>
                        <div><p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Funcionarios</p><p className="text-sm font-bold" style={{ color: planoStatus.current_employees >= planoStatus.max_employees ? '#ef4444' : 'var(--text)' }}>{planoStatus.current_employees}/{planoStatus.max_employees}</p></div>
                        <div><p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Gestores</p><p className="text-sm font-bold" style={{ color: planoStatus.current_gestores >= planoStatus.max_gestores ? '#ef4444' : 'var(--text)' }}>{planoStatus.current_gestores}/{planoStatus.max_gestores === 999 ? 'Ilimitado' : planoStatus.max_gestores}</p></div>
                        <div><p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Valor</p><p className="text-sm font-bold" style={{ color: 'var(--text)' }}>R$ {planoStatus.price?.toFixed(2).replace('.', ',')}/mes</p></div>
                    </div>
                    {planoStatus.plan_expires && (
                        <p className="text-[10px] mt-2" style={{ color: 'var(--muted-2)' }}>Vence em: {new Date(planoStatus.plan_expires).toLocaleDateString('pt-BR')}</p>
                    )}
                    {planoStatus.current_employees >= planoStatus.max_employees && (
                        <div className="mt-3 rounded-lg p-2 flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
                            <AlertTriangle size={14} style={{ color: '#ef4444' }} />
                            <p className="text-[11px]" style={{ color: '#ef4444' }}>Limite de funcionarios atingido. Faca upgrade para cadastrar mais.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Plan cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {planos.map(p => {
                    const active = planoStatus?.plan === p.key;
                    const isUpgrade = planos.findIndex(x => x.key === p.key) > planos.findIndex(x => x.key === planoStatus?.plan);
                    return (
                        <div key={p.key} className="rounded-xl text-center p-5" style={{ background: 'var(--surface)', border: active ? '2px solid var(--sv-gold-primary)' : '1px solid var(--border)' }} data-testid={`plano-${p.key}`}>
                            {active && <span className="text-[10px] px-2 py-0.5 rounded-full mb-2 inline-block font-medium" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' }}>Plano atual</span>}
                            <h3 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{p.name}</h3>
                            <p className="text-2xl font-bold mt-1" style={{ color: 'var(--sv-gold-primary)' }}>R$ {p.price.toFixed(2).replace('.', ',')}<span className="text-xs font-normal" style={{ color: 'var(--muted-2)' }}>/mes</span></p>
                            <p className="text-[10px] mt-1" style={{ color: 'var(--muted-2)' }}>Até {p.max_employees} funcionarios</p>
                            <ul className="mt-4 space-y-2 text-left">{p.features.map((f, i) => (<li key={i} className="text-[11px] flex items-start gap-2" style={{ color: 'var(--muted)' }}><CheckCircle size={11} className="mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />{f}</li>))}</ul>
                            <button
                                onClick={() => !active && isUpgrade && handleUpgrade(p.key)}
                                disabled={active || !isUpgrade || upgrading}
                                className="w-full mt-4 py-2.5 rounded-lg text-xs font-medium transition-all"
                                style={{ background: active ? 'var(--surface-2)' : isUpgrade ? 'var(--sv-gold-primary)' : 'var(--surface-2)', color: active ? 'var(--muted-2)' : isUpgrade ? '#0A0A0A' : 'var(--muted-2)', opacity: active || !isUpgrade ? 0.5 : 1 }}
                                data-testid={`upgrade-${p.key}`}
                            >
                                {active ? 'Ativo' : isUpgrade ? (upgrading ? 'Processando...' : 'Fazer Upgrade') : 'Plano inferior'}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ==================== CONFIG ====================
function ConfigTab({ headers, empresa, setEmpresa }) {
    const [geo, setGeo] = useState(empresa?.geofence || { latitude: null, longitude: null, radius: 200, enabled: false });
    const [cfg, setCfg] = useState(empresa?.config || { precision_limit: 100, require_geo_all: false });
    const [saving, setSaving] = useState(false);

    const handleGetLocation = () => {
        navigator.geolocation.getCurrentPosition(
            (pos) => { setGeo({ ...geo, latitude: pos.coords.latitude, longitude: pos.coords.longitude }); toast.success('Localizacao capturada!'); },
            () => toast.error('Não foi possivel obter localização'), { enableHighAccuracy: true }
        );
    };

    const handleSaveGeo = async () => {
        setSaving(true);
        try { const res = await fetch(`${API}/ponto/empresa/geofence`, { method: 'PUT', headers, body: JSON.stringify(geo) }); if (res.ok) { toast.success('Geofence salva!'); setEmpresa({ ...empresa, geofence: geo }); } } catch { toast.error('Erro'); }
        finally { setSaving(false); }
    };

    const handleSaveCfg = async () => {
        setSaving(true);
        try { const res = await fetch(`${API}/ponto/empresa/config`, { method: 'PUT', headers, body: JSON.stringify(cfg) }); if (res.ok) { toast.success('Config salva!'); setEmpresa({ ...empresa, config: cfg }); } } catch { toast.error('Erro'); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="ponto-config-tab">
            {/* Geofence */}
            <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 mb-3"><MapPin size={16} style={{ color: 'var(--sv-gold-primary)' }} /><span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Geofence da Empresa</span></div>
                <p className="text-xs mb-3" style={{ color: 'var(--muted-2)' }}>Defina o local e raio onde seus funcionarios podem bater ponto.</p>
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text)' }}>Ativar Geofence</span>
                        <button onClick={() => setGeo({ ...geo, enabled: !geo.enabled })} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: geo.enabled ? 'var(--sv-gold-primary)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="geofence-toggle">
                            <div className="w-4 h-4 rounded-full transition-transform" style={{ background: geo.enabled ? 'var(--bg)' : 'var(--muted-2)', transform: geo.enabled ? 'translateX(16px)' : 'translateX(0)' }} />
                        </button>
                    </div>
                    {geo.enabled && (<>
                        <Button onClick={handleGetLocation} variant="outline" className="text-xs w-full" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}><Navigation className="w-3 h-3 mr-1" /> Usar minha localização atual</Button>
                        {geo.latitude && <p className="text-[10px] font-mono" style={{ color: 'var(--muted-2)' }}>Lat: {geo.latitude?.toFixed(6)} | Lng: {geo.longitude?.toFixed(6)}</p>}
                        <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Raio (metros)</label><input type="number" value={geo.radius} onChange={e => setGeo({...geo, radius: parseInt(e.target.value)})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} /></div>
                    </>)}
                    <Button onClick={handleSaveGeo} className="w-full bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" disabled={saving} data-testid="save-geofence">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Geofence'}</Button>
                </div>
            </div>
            {/* Precision Config */}
            <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 mb-3"><Settings size={16} style={{ color: 'var(--sv-gold-primary)' }} /><span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Configurações de Ponto</span></div>
                <div className="space-y-3">
                    <div><label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Limite de precisao GPS (metros)</label>
                        <input type="number" value={cfg.precision_limit} onChange={e => setCfg({...cfg, precision_limit: parseInt(e.target.value)})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="precision-limit" /></div>
                    <div className="flex items-center justify-between">
                        <div><span className="text-xs" style={{ color: 'var(--text)' }}>GPS obrigatorio em todas marcações</span><p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Não apenas na entrada</p></div>
                        <button onClick={() => setCfg({ ...cfg, require_geo_all: !cfg.require_geo_all })} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: cfg.require_geo_all ? 'var(--sv-gold-primary)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="require-geo-toggle">
                            <div className="w-4 h-4 rounded-full transition-transform" style={{ background: cfg.require_geo_all ? 'var(--bg)' : 'var(--muted-2)', transform: cfg.require_geo_all ? 'translateX(16px)' : 'translateX(0)' }} />
                        </button>
                    </div>
                    <Button onClick={handleSaveCfg} className="w-full bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" disabled={saving} data-testid="save-config">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Configurações'}</Button>
                </div>
            </div>
            {/* Telegram Alerts */}
            <TelegramAlertsConfig headers={headers} />
            {/* Daily Summary */}
            <DailySummaryConfig headers={headers} />
            {/* Weekly Report */}
            <WeeklyReportConfig headers={headers} />
            {/* Limitations */}
            <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <div className="flex items-center gap-2 mb-3"><Shield size={16} style={{ color: 'var(--sv-gold-primary)' }} /><span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Limitacoes Tecnicas</span></div>
                <div className="space-y-2 text-[11px]" style={{ color: 'var(--muted)' }}>
                    <p>GPS via navegador depende da permissao do usuario e pode ter precisao variavel.</p>
                    <p>Em ambiente web não e possivel impedir completamente mock location.</p>
                    <p>Para seguranca maxima, considere app nativo com validacao de dispositivo.</p>
                </div>
            </div>
        </div>
    );
}

// ==================== SHARED COMPONENTS ====================
function StatCard({ label, value, icon: Icon, color = 'var(--sv-gold-primary)', sub }) {
    return (
        <div className="rounded-xl p-4 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <Icon size={18} style={{ color, margin: '0 auto 6px' }} />
            <p className="text-lg font-bold" style={{ color: 'var(--text)' }}>{value}</p>
            <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{label}</p>
            {sub && <p className="text-[9px] mt-0.5" style={{ color: 'var(--muted-2)' }}>{sub}</p>}
        </div>
    );
}

function SRow({ label, value, ok }) {
    return (<div className="flex items-center justify-between"><span className="text-[11px]" style={{ color: 'var(--muted-2)' }}>{label}</span><span className="text-[11px] font-medium" style={{ color: ok ? '#22c55e' : '#ef4444' }}>{value}</span></div>);
}

function TipoIcon({ tipo }) {
    const map = { entrada: { bg: 'rgba(34,197,94,0.1)', color: '#22c55e', icon: Play }, inicio_intervalo: { bg: 'rgba(234,179,8,0.1)', color: '#eab308', icon: Coffee }, fim_intervalo: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6', icon: Coffee }, saída: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444', icon: LogOutIcon } };
    const c = map[tipo] || map.saida;
    return <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: c.bg }}><c.icon size={10} style={{ color: c.color }} /></div>;
}

function GeoBadge({ inside }) {
    return <span className="text-[8px] px-1.5 py-0.5 rounded" style={{ background: inside ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: inside ? '#22c55e' : '#ef4444' }}>{inside ? 'Dentro' : 'Fora'}</span>;
}

function Spinner() { return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--sv-gold-primary)' }} /></div>; }

function EmptyState({ icon: Icon, text }) {
    return <div className="text-center py-12"><Icon size={28} className="mx-auto mb-2" style={{ color: 'var(--muted-2)' }} /><p className="text-xs" style={{ color: 'var(--muted-2)' }}>{text}</p></div>;
}

function tipoLabel(t) { return { entrada: 'Entrada', inicio_intervalo: 'Inicio Intervalo', fim_intervalo: 'Fim Intervalo', saída: 'Saída' }[t] || t; }
function fmtTime(ts) { return ts ? new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''; }
function fmtDate(d) { return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' }); }

function TelegramAlertsConfig({ headers }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [cfg, setCfg] = useState({ enabled: false, events: [], chat_id: '' });
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);

    useEffect(() => {
        fetch(`${API}/ponto/telegram-alerts`, { headers })
            .then(r => r.json()).then(d => {
                setData(d);
                setCfg(d.config || { enabled: false, events: [], chat_id: '' });
                setLoading(false);
            }).catch(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/ponto/telegram-alerts`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(cfg) });
            if (r.ok) toast.success('Alertas Telegram salvos!');
            else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        setSaving(false);
    };

    const loadHistory = async () => {
        setShowHistory(!showHistory);
        if (!showHistory) {
            const r = await fetch(`${API}/ponto/telegram-alerts/history?limit=20`, { headers });
            const d = await r.json();
            setHistory(Array.isArray(d) ? d : []);
        }
    };

    const toggleEvent = (e) => {
        setCfg(prev => ({ ...prev, events: prev.events.includes(e) ? prev.events.filter(x => x !== e) : [...prev.events, e] }));
    };

    if (loading) return null;
    if (!data) return null;

    const allowed = data.allowed_events || [];
    const allEvents = data.all_events || [];
    const labels = data.labels || {};
    const plan = data.plan || 'free';

    return (
        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="telegram-alerts-config">
            <div className="flex items-center gap-2 mb-3">
                <Send size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Alertas via Telegram</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>{plan}</span>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted-2)' }}>Receba notificações no Telegram quando eventos de ponto acontecerem.</p>

            {allowed.length === 0 ? (
                <div className="text-center py-6 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                    <Bell size={20} className="mx-auto mb-2" style={{ color: 'var(--muted-2)' }} />
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Alertas Telegram não disponíveis no plano {plan}.</p>
                    <a href="/planos" className="text-[10px] font-medium mt-1 inline-block" style={{ color: 'var(--sv-gold-primary)' }}>Fazer upgrade &#8594;</a>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text)' }}>Ativar alertas</span>
                        <button onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: cfg.enabled ? 'var(--sv-gold-primary)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="telegram-alert-toggle">
                            <div className="w-4 h-4 rounded-full transition-transform" style={{ background: cfg.enabled ? 'var(--bg)' : 'var(--muted-2)', transform: cfg.enabled ? 'translateX(16px)' : 'translateX(0)' }} />
                        </button>
                    </div>

                    {cfg.enabled && (<>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Chat ID do grupo/gestor (opcional se ja configurado nas Conexões)</label>
                            <input value={cfg.chat_id} onChange={e => setCfg({...cfg, chat_id: e.target.value})} placeholder="Ex: -100123456789" className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="alert-chat-id" />
                        </div>
                        <div>
                            <label className="text-[10px] block mb-2" style={{ color: 'var(--muted-2)' }}>Eventos que disparam alerta:</label>
                            <div className="flex flex-wrap gap-1.5">
                                {allEvents.map(e => {
                                    const isAllowed = allowed.includes(e);
                                    const isActive = cfg.events.includes(e);
                                    return (
                                        <button key={e} onClick={() => isAllowed && toggleEvent(e)} disabled={!isAllowed}
                                            className="px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all"
                                            style={{
                                                background: isActive ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface-2)',
                                                border: `1px solid ${isActive ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`,
                                                color: isAllowed ? (isActive ? 'var(--sv-gold-primary)' : 'var(--text)') : 'var(--disabled)',
                                                opacity: isAllowed ? 1 : 0.5,
                                                cursor: isAllowed ? 'pointer' : 'not-allowed',
                                            }}
                                            data-testid={`alert-event-${e}`}>
                                            {labels[e] || e}
                                            {!isAllowed && ' 🔒'}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </>)}

                    <div className="flex gap-2">
                        <Button onClick={handleSave} className="flex-1 bg-[var(--sv-action)] hover:bg-[var(--sv-action-hover)] text-[var(--sv-on-action)] text-xs" disabled={saving} data-testid="save-telegram-alerts">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Alertas'}
                        </Button>
                        <Button variant="outline" onClick={loadHistory} className="text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} data-testid="view-alert-history">
                            Histórico
                        </Button>
                    </div>

                    {showHistory && (
                        <div className="rounded-lg p-3 space-y-1.5" style={{ background: 'var(--surface-2)' }}>
                            <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--muted)' }}>Últimos alertas enviados:</p>
                            {history.length === 0 ? (
                                <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Nenhum alerta enviado ainda.</p>
                            ) : history.map(h => (
                                <div key={h.alert_id} className="flex items-center justify-between text-[10px] py-1" style={{ borderBottom: '1px solid var(--border)' }}>
                                    <span style={{ color: 'var(--text)' }}>{h.user_name} - {labels[h.tipo] || h.tipo}</span>
                                    <span style={{ color: 'var(--muted-2)' }}>{h.timestamp?.slice(0, 16).replace('T', ' ')}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function DailySummaryConfig({ headers }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [cfg, setCfg] = useState({ enabled: false, hour: 18, chat_id: '' });

    useEffect(() => {
        fetch(`${API}/ponto/daily-summary/config`, { headers })
            .then(r => r.json()).then(d => {
                setData(d);
                setCfg(d.config || { enabled: false, hour: 18, chat_id: '' });
                setLoading(false);
            }).catch(() => setLoading(false));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/ponto/daily-summary/config`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(cfg) });
            if (r.ok) toast.success('Resumo diário configurado!');
            else { const e = await r.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexão'); }
        setSaving(false);
    };

    const handleSendNow = async () => {
        setSending(true);
        try {
            const r = await fetch(`${API}/ponto/daily-summary/send`, { method: 'POST', headers });
            const d = await r.json();
            if (r.ok) toast.success(`Resumo enviado! ${d.summary?.presentes}/${d.summary?.total_colabs} presentes.`);
            else toast.error(d.detail || 'Erro ao enviar');
        } catch { toast.error('Erro de conexão'); }
        setSending(false);
    };

    if (loading || !data) return null;
    const access = data.access;
    const plan = data.plan || 'free';

    return (
        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="daily-summary-config">
            <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Resumo Diário via Telegram</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>{plan}</span>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted-2)' }}>Receba um resumo automático da operação da equipe no Telegram todo dia.</p>

            {!access ? (
                <div className="text-center py-6 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                    <BarChart3 size={20} className="mx-auto mb-2" style={{ color: 'var(--muted-2)' }} />
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Resumo diário não disponível no plano {plan}.</p>
                    <a href="/planos" className="text-[10px] font-medium mt-1 inline-block" style={{ color: 'var(--sv-gold-primary)' }}>Fazer upgrade &#8594;</a>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text)' }}>Ativar resumo diário</span>
                        <button onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: cfg.enabled ? 'var(--sv-gold-primary)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="daily-summary-toggle">
                            <div className="w-4 h-4 rounded-full transition-transform" style={{ background: cfg.enabled ? 'var(--bg)' : 'var(--muted-2)', transform: cfg.enabled ? 'translateX(16px)' : 'translateX(0)' }} />
                        </button>
                    </div>
                    {cfg.enabled && (<>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Horário de envio (UTC)</label>
                                <select value={cfg.hour} onChange={e => setCfg({...cfg, hour: parseInt(e.target.value)})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="summary-hour-select">
                                    {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Chat ID (opcional)</label>
                                <input value={cfg.chat_id} onChange={e => setCfg({...cfg, chat_id: e.target.value})} placeholder="Usar padrão" className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="summary-chat-id" />
                            </div>
                        </div>
                        <div className="text-[10px] p-2 rounded-lg" style={{ background: access === 'simple' ? 'rgba(245,158,11,0.06)' : 'rgba(16,185,129,0.06)', color: access === 'simple' ? '#F59E0B' : '#10B981' }}>
                            {access === 'simple' ? 'Plano Start: resumo básico (presentes, ausentes)' : 'Plano completo: resumo com entradas, saídas, geofence, ajustes'}
                        </div>
                    </>)}
                    <div className="flex gap-2">
                        <Button onClick={handleSave} className="flex-1 text-xs" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} disabled={saving} data-testid="save-daily-summary">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Config'}
                        </Button>
                        {cfg.enabled && (
                            <Button variant="outline" onClick={handleSendNow} className="text-xs" disabled={sending} data-testid="send-summary-now">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar Agora'}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function WeeklyReportConfig({ headers }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [cfg, setCfg] = useState({ enabled: false, day_of_week: 5, hour: 9, channels: ['email'], recipients: [''], chat_id: '' });
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [unidades, setUnidades] = useState([]);
    const [downloadMode, setDownloadMode] = useState('consolidado');
    const [downloadUnidade, setDownloadUnidade] = useState('');
    const [showDownloadOpts, setShowDownloadOpts] = useState(false);

    useEffect(() => {
        fetch(`${API}/ponto/weekly-report/config`, { headers })
            .then(r => r.json()).then(d => {
                setData(d);
                const c = d.config || {};
                setCfg({ enabled: c.enabled || false, day_of_week: c.day_of_week ?? 5, hour: c.hour ?? 9, channels: c.channels || ['email'], recipients: c.recipients?.length ? c.recipients : [''], chat_id: c.chat_id || '' });
                setLoading(false);
            }).catch(() => setLoading(false));
        fetch(`${API}/ponto/unidades`, { headers })
            .then(r => r.ok ? r.json() : []).then(u => setUnidades(Array.isArray(u) ? u : [])).catch(() => {});
    }, []);

    const handleSave = async () => {
        setSaving(true);
        const payload = { ...cfg, recipients: cfg.recipients.filter(r => r.trim()) };
        try {
            const r = await fetch(`${API}/ponto/weekly-report/config`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            if (r.ok) toast.success('Relatorio semanal configurado!');
            else { const e = await r.json(); toast.error(e.detail || 'Erro'); }
        } catch { toast.error('Erro de conexao'); }
        setSaving(false);
    };

    const handleSendNow = async () => {
        setSending(true);
        try {
            const r = await fetch(`${API}/ponto/weekly-report/send`, { method: 'POST', headers });
            const d = await r.json();
            if (r.ok) toast.success(`Relatorio enviado! Email: ${d.results?.email ? 'OK' : 'N/A'}, Telegram: ${d.results?.telegram ? 'OK' : 'N/A'}`);
            else toast.error(d.detail || 'Erro');
        } catch { toast.error('Erro de conexao'); }
        setSending(false);
    };

    const handleDownload = () => {
        let url = `${API}/ponto/weekly-report/download?mode=${downloadMode}`;
        if (downloadMode === 'unidade' && downloadUnidade) {
            url = `${API}/ponto/weekly-report/download?unidade_id=${downloadUnidade}`;
        }
        window.open(url, '_blank');
        setShowDownloadOpts(false);
    };

    const loadHistory = async () => {
        setShowHistory(!showHistory);
        if (!showHistory) {
            const r = await fetch(`${API}/ponto/weekly-report/history?limit=10`, { headers });
            setHistory(await r.json());
        }
    };

    const toggleChannel = (ch) => {
        setCfg(prev => ({ ...prev, channels: prev.channels.includes(ch) ? prev.channels.filter(c => c !== ch) : [...prev.channels, ch] }));
    };

    const updateRecipient = (i, v) => {
        const r = [...cfg.recipients];
        r[i] = v;
        setCfg({ ...cfg, recipients: r });
    };

    const dayLabels = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado', 'Domingo'];

    if (loading || !data) return null;
    const access = data.access;
    const plan = data.plan || 'free';

    return (
        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="weekly-report-config">
            <div className="flex items-center gap-2 mb-1">
                <FileText size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>Relatório Semanal PDF</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto" style={{ background: access === 'full' ? 'rgba(139,92,246,0.12)' : access === 'basic' ? 'rgba(59,130,246,0.12)' : 'rgba(107,114,128,0.12)', color: access === 'full' ? '#8B5CF6' : access === 'basic' ? '#3B82F6' : '#6B7280' }}>
                    {access === 'full' ? 'PRO' : access === 'basic' ? 'GESTAO' : plan}
                </span>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--muted-2)' }}>Relatório semanal automático com métricas de presença, pontualidade e tendências.</p>

            {!access ? (
                <div className="text-center py-6 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                    <FileText size={20} className="mx-auto mb-2" style={{ color: 'var(--muted-2)' }} />
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>Relatório semanal disponível a partir do plano Gestão.</p>
                    <a href="/planos" className="text-[10px] font-medium mt-1 inline-block" style={{ color: 'var(--sv-gold-primary)' }}>Fazer upgrade &#8594;</a>
                </div>
            ) : (
                <div className="space-y-3">
                    {/* Toggle */}
                    <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--text)' }}>Ativar envio automático</span>
                        <button onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })} className="w-10 h-6 rounded-full flex items-center transition-colors" style={{ background: cfg.enabled ? 'var(--sv-gold-primary)' : 'var(--surface-3)', border: '1px solid var(--border)', padding: '0 2px' }} data-testid="weekly-report-toggle">
                            <div className="w-4 h-4 rounded-full transition-transform" style={{ background: cfg.enabled ? 'var(--bg)' : 'var(--muted-2)', transform: cfg.enabled ? 'translateX(16px)' : 'translateX(0)' }} />
                        </button>
                    </div>

                    {cfg.enabled && (<>
                        {/* Schedule */}
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Dia da semana</label>
                                <select value={cfg.day_of_week} onChange={e => setCfg({...cfg, day_of_week: parseInt(e.target.value)})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="weekly-day-select">
                                    {dayLabels.map((d, i) => <option key={i} value={i}>{d}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Horário (UTC)</label>
                                <select value={cfg.hour} onChange={e => setCfg({...cfg, hour: parseInt(e.target.value)})} className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="weekly-hour-select">
                                    {Array.from({length: 24}, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Channels */}
                        <div>
                            <label className="text-[10px] block mb-1.5" style={{ color: 'var(--muted-2)' }}>Canais de envio</label>
                            <div className="flex gap-2">
                                {[
                                    { key: 'email', label: 'Email', icon: Mail, available: true },
                                    { key: 'telegram', label: 'Telegram', icon: Send, available: true },
                                    { key: 'whatsapp', label: 'WhatsApp', icon: Bell, available: false },
                                ].map(ch => (
                                    <button key={ch.key} onClick={() => ch.available && toggleChannel(ch.key)} disabled={!ch.available}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all"
                                        style={{
                                            background: cfg.channels.includes(ch.key) ? 'rgba(var(--sv-accent-rgb), 0.12)' : 'var(--surface-2)',
                                            border: `1px solid ${cfg.channels.includes(ch.key) ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`,
                                            color: ch.available ? (cfg.channels.includes(ch.key) ? 'var(--sv-gold-primary)' : 'var(--text)') : 'var(--disabled)',
                                            opacity: ch.available ? 1 : 0.5,
                                        }}
                                        data-testid={`channel-${ch.key}`}>
                                        <ch.icon size={10} />
                                        {ch.label} {!ch.available && ' (em breve)'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Recipients */}
                        {cfg.channels.includes('email') && (
                            <div>
                                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Destinatarios (email)</label>
                                {cfg.recipients.map((r, i) => (
                                    <div key={i} className="flex gap-1.5 mb-1">
                                        <input value={r} onChange={e => updateRecipient(i, e.target.value)} placeholder="email@exemplo.com" className="flex-1 rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid={`recipient-${i}`} />
                                        {cfg.recipients.length > 1 && <button onClick={() => setCfg({...cfg, recipients: cfg.recipients.filter((_, j) => j !== i)})} className="px-2 text-xs" style={{ color: '#EF4444' }}>X</button>}
                                    </div>
                                ))}
                                {cfg.recipients.length < 5 && (
                                    <button onClick={() => setCfg({...cfg, recipients: [...cfg.recipients, '']})} className="text-[10px] font-medium mt-1" style={{ color: 'var(--sv-gold-primary)' }}>+ Adicionar destinatario</button>
                                )}
                            </div>
                        )}

                        {/* Telegram chat ID */}
                        {cfg.channels.includes('telegram') && (
                            <div>
                                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Chat ID Telegram (opcional)</label>
                                <input value={cfg.chat_id} onChange={e => setCfg({...cfg, chat_id: e.target.value})} placeholder="Usar padrão das Conexões" className="w-full rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="weekly-chat-id" />
                            </div>
                        )}

                        {/* Access level info */}
                        <div className="text-[10px] p-2 rounded-lg" style={{ background: access === 'basic' ? 'rgba(59,130,246,0.06)' : 'rgba(139,92,246,0.06)', color: access === 'basic' ? '#3B82F6' : '#8B5CF6' }}>
                            {access === 'basic' ? 'Gestão: relatório básico (resumo executivo)' : 'Pro/Enterprise: relatório completo com detalhamento diário'}
                        </div>
                    </>)}

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={handleSave} className="flex-1 text-xs" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} disabled={saving} data-testid="save-weekly-report">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar Config'}
                        </Button>
                        <div className="relative">
                            <Button variant="outline" onClick={() => setShowDownloadOpts(!showDownloadOpts)} className="text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} data-testid="download-weekly-report">
                                <FileDown size={12} className="mr-1" /> PDF
                            </Button>
                            {showDownloadOpts && (
                                <div className="absolute top-full right-0 mt-1 z-50 rounded-xl p-3 min-w-[260px] space-y-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }} data-testid="pdf-download-options">
                                    <p className="text-[10px] font-semibold mb-1" style={{ color: 'var(--text)' }}>Tipo de relatorio:</p>
                                    {[
                                        { value: 'consolidado', label: 'Consolidado (geral)' },
                                        ...(unidades.length >= 2 ? [{ value: 'detalhado', label: 'Consolidado + Detalhado por Unidade' }] : []),
                                        ...(unidades.length > 0 ? [{ value: 'unidade', label: 'Filtrado por Unidade' }] : []),
                                    ].map(opt => (
                                        <label key={opt.value} className="flex items-center gap-2 cursor-pointer p-1.5 rounded-lg transition-colors" style={{ background: downloadMode === opt.value ? 'rgba(var(--sv-accent-rgb), 0.08)' : 'transparent' }}>
                                            <input type="radio" name="dlMode" value={opt.value} checked={downloadMode === opt.value} onChange={() => setDownloadMode(opt.value)} className="accent-[var(--sv-accent)]" />
                                            <span className="text-xs" style={{ color: 'var(--text)' }}>{opt.label}</span>
                                        </label>
                                    ))}
                                    {downloadMode === 'unidade' && (
                                        <select value={downloadUnidade} onChange={e => setDownloadUnidade(e.target.value)} className="w-full rounded-lg px-2 py-1.5 text-xs mt-1" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="pdf-unidade-select">
                                            <option value="">Selecione uma unidade</option>
                                            {unidades.map(u => <option key={u.unidade_id} value={u.unidade_id}>{u.name}</option>)}
                                        </select>
                                    )}
                                    <Button onClick={handleDownload} className="w-full text-xs mt-1" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} disabled={downloadMode === 'unidade' && !downloadUnidade} data-testid="pdf-download-confirm">
                                        <FileDown size={12} className="mr-1" /> Baixar PDF
                                    </Button>
                                </div>
                            )}
                        </div>
                        {cfg.enabled && (
                            <Button variant="outline" onClick={handleSendNow} className="text-xs" disabled={sending} data-testid="send-weekly-now">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar Agora'}
                            </Button>
                        )}
                        <Button variant="outline" onClick={loadHistory} className="text-xs" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} data-testid="weekly-report-history">
                            Histórico
                        </Button>
                    </div>

                    {/* History */}
                    {showHistory && (
                        <div className="rounded-lg p-3 space-y-1.5" style={{ background: 'var(--surface-2)' }}>
                            <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--muted)' }}>Últimos relatórios:</p>
                            {history.length === 0 ? (
                                <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>Nenhum relatório enviado ainda.</p>
                            ) : history.map(h => (
                                <div key={h.report_id} className="flex items-center justify-between text-[10px] py-1" style={{ borderBottom: '1px solid var(--border)' }}>
                                    <span style={{ color: 'var(--text)' }}>{h.date}</span>
                                    <div className="flex gap-2">
                                        {h.results?.email && <span style={{ color: '#10B981' }}>Email OK</span>}
                                        {h.results?.telegram && <span style={{ color: '#3B82F6' }}>Telegram OK</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

